class MonolytWishlist {
  constructor() {
    this.key = 'monolyt:wishlist:v1';
    this.memoryItems = [];
    this.memoryDirty = false;
    this.storageMode = 'memory';
    this.items = this.read();
    this.unregisterOverlay = null;
    this.bind();
    this.observeDynamicControls();
    this.render();
    document.documentElement.classList.add('monolyt-wishlist-managed');
    document.addEventListener('DOMContentLoaded', () => {
      this.render();
      import('@theme/optical-count').then(({ centerOpticalCount }) => {
        this.centerOpticalCount = centerOpticalCount;
        this.renderControls();
      }).catch(() => {});
    }, { once: true });
    window.addEventListener('storage', (event) => {
      if (event.key !== null && event.key !== this.key) return;
      // A remote local update is authoritative, including removeItem() and clear().
      if (event.storageArea === this.getStorage('local')) {
        this.memoryItems = [];
        this.memoryDirty = false;
        try { this.getStorage('session')?.removeItem(this.key); } catch (_error) {}
        try { this.getStorage('session')?.removeItem(this.key + ':fallback'); } catch (_error) {}
        let parsed;
        try { parsed = JSON.parse(event.newValue ?? '[]'); } catch (_error) { parsed = []; }
        this.items = this.normalizeItems(parsed);
        this.memoryItems = this.items;
        this.storageMode = 'local';
      } else {
        this.items = this.read();
      }
      this.render();
    });
    window.addEventListener('pageshow', (event) => {
      if (!event.persisted) return;
      this.items = this.read();
      this.render();
    });
    customElements.whenDefined('header-component').then(() => this.registerOverlay());
  }

  get headerComponent() {
    return document.querySelector('header-component');
  }

  get drawer() {
    return document.querySelector('[data-wishlist-drawer]');
  }

  get panel() {
    return this.drawer?.querySelector('.monolyt-wishlist__panel') || null;
  }

  get trigger() {
    return document.querySelector('[data-wishlist-open]');
  }

  getStorage(mode) {
    try {
      return mode === 'local' ? window.localStorage : window.sessionStorage;
    } catch (_error) {
      return null;
    }
  }

  sanitizeProductUrl(value) {
    const url = typeof value === 'string' ? value.trim() : '';
    return /^\/(?!\/)[^\u0000-\u001F\u007F]*$/.test(url) ? url : '#';
  }

  sanitizeImageUrl(value) {
    const url = typeof value === 'string' ? value.trim() : '';
    if (/^\/(?!\/)[^\u0000-\u001F\u007F]*$/.test(url)) return url;
    if (/^\/\/[A-Za-z0-9.-]+(?::\d+)?\/[^\u0000-\u001F\u007F]*$/.test(url)) return url;
    if (/^https:\/\/[^\s\u0000-\u001F\u007F]+$/i.test(url)) return url;
    return '';
  }

  normalizeItems(value) {
    if (!Array.isArray(value)) return [];

    const handles = new Set();
    const items = [];

    for (const rawItem of value) {
      if (!rawItem || typeof rawItem !== 'object') continue;

      const handle = typeof rawItem.handle === 'string' ? rawItem.handle.trim() : '';
      if (!handle || handles.has(handle)) continue;

      handles.add(handle);
      items.push({
        handle,
        wishlistTitle: typeof rawItem.wishlistTitle === 'string' ? rawItem.wishlistTitle : '',
        wishlistUrl: this.sanitizeProductUrl(rawItem.wishlistUrl),
        wishlistImage: this.sanitizeImageUrl(rawItem.wishlistImage),
        wishlistPrice: typeof rawItem.wishlistPrice === 'string' ? rawItem.wishlistPrice : '',
        addedAt: typeof rawItem.addedAt === 'string' ? rawItem.addedAt : '',
      });

      if (items.length === 60) break;
    }

    return items;
  }

  registerOverlay() {
    if (this.unregisterOverlay) return;

    this.unregisterOverlay = this.headerComponent?.registerOverlay?.(this, {
      id: 'wishlist',
      getPanel: () => this.panel,
      getTrigger: () => this.trigger,
      close: (options = {}) => this.close({ restoreFocus: options.restoreFocus === true }),
    });
  }

  readFallbackMetadata(session, sessionPayload) {
    try {
      const metadata = JSON.parse(session?.getItem(this.key + ':fallback') ?? 'null');
      if (metadata?.storageFallback !== 1 || typeof metadata.sessionPayload !== 'string' ||
        metadata.sessionPayload !== sessionPayload || typeof metadata.localPayloadKnown !== 'boolean' ||
        !(metadata.localPayload === null || typeof metadata.localPayload === 'string')) return null;
      return metadata;
    } catch (_error) {
      return null;
    }
  }

  read() {
    // A failed persistence attempt retains its newer state in this instance.
    // Authoritative storage events reset memory before calling read().
    if (this.memoryDirty) return this.normalizeItems(this.memoryItems);
    const payloads = new Map();
    for (const mode of ['local', 'session']) {
      try {
        const storage = this.getStorage(mode);
        if (storage) payloads.set(mode, storage.getItem(this.key));
      } catch (_error) {}
    }

    const fallback = this.readFallbackMetadata(this.getStorage('session'), payloads.get('session'));
    // Metadata only claims the exact session array it accompanied. An older writer
    // changing that array invalidates the claim; missing/corrupt metadata is not authority.
    const useFallback = fallback && (!payloads.has('local') || !fallback.localPayloadKnown || payloads.get('local') === fallback.localPayload);
    const modes = useFallback ? ['session', 'local'] : ['local', 'session'];

    for (const mode of modes) {
      const payload = payloads.get(mode);
      if (payload == null) continue;
      // A changed readable local tier retires a known fallback, even after deletion.
      if (mode === 'session' && fallback && !useFallback) continue;
      let parsed;
      try { parsed = JSON.parse(payload); } catch (_error) { parsed = []; }
      const items = this.normalizeItems(parsed);
      this.storageMode = mode;
      this.memoryItems = items;
      this.memoryDirty = false;
      if (JSON.stringify(items) !== payload) {
        try { this.getStorage(mode)?.setItem(this.key, JSON.stringify(items)); } catch (_error) {}
      }
      return items;
    }

    const memoryOnly = this.memoryDirty;
    this.storageMode = memoryOnly ? 'memory' : payloads.has('local') ? 'local' : payloads.has('session') ? 'session' : 'memory';
    // Readable absence is empty unless both writes previously failed in this instance.
    // Explicit storage deletion events clear memory before reaching this path.
    if (payloads.size && !memoryOnly) this.memoryItems = [];
    return this.normalizeItems(this.memoryItems);
  }

  write() {
    this.items = this.normalizeItems(this.items);
    this.memoryItems = this.items;
    this.memoryDirty = true;
    const payload = JSON.stringify(this.items);
    const local = this.getStorage('local');
    const session = this.getStorage('session');
    let localPayload = null;
    let localPayloadKnown = false;
    try {
      if (local) { localPayload = local.getItem(this.key); localPayloadKnown = true; }
    } catch (_error) {}
    const metadataPayload = JSON.stringify({ storageFallback: 1, localPayload, localPayloadKnown, sessionPayload: payload });
    const persistFallback = () => {
      if (!session) throw new Error('Session storage unavailable');
      // Keep the canonical session key readable by old versions. Bind metadata to
      // the exact array bytes, so a partial/older write cannot claim newer authority.
      session.setItem(this.key, payload);
      session.setItem(this.key + ':fallback', metadataPayload);
    };
    let sessionPrepared = false;
    let previousSessionPayload;
    try { previousSessionPayload = session?.getItem(this.key); } catch (_error) {}
    const previousFallback = this.readFallbackMetadata(session, previousSessionPayload);
    if (previousFallback) {
      // Refresh active fallback data before recovery; do not delete the last saved
      // array before another tier has accepted it. Partial two-key writes stay unconfirmed.
      try {
        persistFallback();
        sessionPrepared = true;
      } catch (_error) {
        // Session failure does not rule out recovered local storage.
      }
    }

    try {
      if (!local) throw new Error('Local storage unavailable');
      local.setItem(this.key, payload);
      let metadataRetired = !session;
      let sessionRetired = !session;
      try { session?.removeItem(this.key + ':fallback'); metadataRetired = true; } catch (_error) {}
      try { session?.removeItem(this.key); sessionRetired = true; } catch (_error) {}
      let localConfirmed = false;
      let remainingSessionPayload;
      try { localConfirmed = local.getItem(this.key) === payload; } catch (_error) {}
      try { remainingSessionPayload = session?.getItem(this.key); } catch (_error) {}
      // Confirm precedence, not merely setItem success: an unknown baseline with
      // unretired stale metadata could otherwise mask the recovered local write.
      const precedenceConfirmed = metadataRetired || sessionRetired ||
        remainingSessionPayload === payload ||
        (previousFallback?.localPayloadKnown && payload !== previousFallback.localPayload);
      if (localConfirmed && precedenceConfirmed) {
        this.storageMode = !metadataRetired && !sessionRetired && sessionPrepared ? 'session' : 'local';
        this.memoryDirty = false;
        return true;
      }
    } catch (_error) {
      // Try the tab-scoped fallback when local storage rejects this write.
    }

    try {
      if (!sessionPrepared) persistFallback();
      this.storageMode = 'session';
      this.memoryDirty = false;
      return true;
    } catch (_error) {}

    this.storageMode = 'memory';
    return false;
  }

  clearAll() {
    this.items = [];
    this.memoryItems = [];
    this.memoryDirty = true;
    let clearedMode = null;

    for (const mode of ['local', 'session']) {
      const storage = this.getStorage(mode);
      if (!storage) continue;

      try {
        storage.removeItem(this.key);
        clearedMode ||= mode;
      } catch (_error) {
        // Continue so every available tier forgets the canonical Wishlist key.
      }
    }

    try { this.getStorage('session')?.removeItem(this.key + ':fallback'); } catch (_error) {}
    this.storageMode = clearedMode || 'memory';
    const session = this.getStorage('session');
    const local = this.getStorage('local');
    try {
      if (local && session && local.getItem(this.key) === null && session.getItem(this.key) === null) this.memoryDirty = false;
    } catch (_error) {}
    this.render();
  }

  has(handle) {
    return this.items.some((item) => item.handle === handle);
  }

  toggle(data) {
    const item = {
      handle: data.wishlistHandle || data.handle,
      wishlistTitle: data.wishlistTitle || data.title || '',
      wishlistUrl: data.wishlistUrl || data.url || '#',
      wishlistImage: data.wishlistImage || data.image || '',
      wishlistPrice: data.wishlistPrice || data.price || '',
    };

    if (!item.handle) return;

    if (this.has(item.handle)) {
      this.items = this.items.filter((savedItem) => savedItem.handle !== item.handle);
    } else {
      this.items = [{ ...item, addedAt: new Date().toISOString() }, ...this.items].slice(0, 60);
    }

    this.write();
    this.render();
  }

  bind() {
    document.addEventListener('click', (event) => {
      const toggle = event.target.closest('[data-wishlist-toggle]');
      const drawerTrigger = event.target.closest('[data-wishlist-open]');
      const close = event.target.closest('[data-wishlist-close]');
      const remove = event.target.closest('[data-wishlist-remove]');
      const clear = event.target.closest('[data-wishlist-clear]');

      if (toggle) {
        event.preventDefault();
        event.stopPropagation();
        this.toggle(toggle.dataset);
        return;
      }

      if (drawerTrigger) {
        event.preventDefault();
        this.toggleDrawer();
        return;
      }

      if (close) {
        event.preventDefault();
        this.close();
        return;
      }

      if (clear) {
        event.preventDefault();
        this.clearAll();
        return;
      }

      if (remove) {
        event.preventDefault();
        this.items = this.items.filter((item) => item.handle !== remove.dataset.wishlistRemove);
        this.write();
        this.render();
      }
    });

  }

  observeDynamicControls() {
    this.controlsObserver = new MutationObserver((mutations) => {
      const controlsChanged = mutations.some((mutation) => {
        if (mutation.type === 'attributes') {
          return mutation.target instanceof Element && mutation.target.matches('[data-wishlist-toggle]');
        }

        // Parsing may add an icon's label/count after its containing button.
        if (mutation.target instanceof Element && mutation.target.closest('[data-wishlist-toggle], [data-wishlist-count]')) return true;

        return [...mutation.addedNodes].some(
          (node) =>
            node instanceof Element &&
            (node.matches('[data-wishlist-count], [data-wishlist-toggle]') ||
              node.querySelector('[data-wishlist-count], [data-wishlist-toggle]'))
        );
      });

      if (controlsChanged) this.renderControls();
    });

    this.controlsObserver.observe(document.documentElement, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['data-wishlist-handle'],
    });
  }

  renderControls() {
    document.querySelectorAll('[data-wishlist-count]').forEach((element) => {
      const count = element.querySelector('[data-monolyt-optical-count]');
      if (!count) return;
      if (count.textContent !== String(this.items.length)) count.textContent = this.items.length;
      this.centerOpticalCount?.(count);
      element.hidden = this.items.length === 0;
      element.closest('[data-wishlist-open]')?.setAttribute('data-wishlist-ready', '');
    });

    document.querySelectorAll('[data-wishlist-toggle]').forEach((button) => {
      const active = this.has(button.dataset.wishlistHandle);
      const label = button.querySelector('[data-wishlist-card-label]');
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-pressed', active ? 'true' : 'false');
      const addLabel = button.dataset.wishlistAddLabel || 'Save item';
      const removeLabel = button.dataset.wishlistRemoveLabel || 'Remove from saved items';
      const saveLabel = button.dataset.wishlistSaveLabel || 'Save';
      const savedLabel = button.dataset.wishlistSavedLabel || 'Saved';
      button.setAttribute('aria-label', active ? removeLabel : addLabel);
      const text = active ? savedLabel : saveLabel;
      if (label && label.textContent !== text) label.textContent = text;
      button.setAttribute('data-wishlist-ready', '');
    });
  }

  render() {
    this.renderControls();

    const list = document.querySelector('[data-wishlist-items]');
    const empty = document.querySelector('[data-wishlist-empty]');
    const clear = document.querySelector('[data-wishlist-clear]');
    const storageStatus = document.querySelector('[data-wishlist-storage-status]');
    if (clear) clear.disabled = this.items.length === 0;
    if (storageStatus) storageStatus.hidden = this.storageMode === 'local';
    if (!list || !empty) return;

    empty.hidden = this.items.length > 0;
    const drawer = document.querySelector('[data-wishlist-drawer]');
    const removeLabel = drawer?.dataset.wishlistRemoveLabel || 'Remove';
    const removeIcon = drawer?.querySelector('[data-wishlist-remove-icon]')?.innerHTML.trim()
      || '<span aria-hidden="true">×</span>';

    list.innerHTML = this.items
      .map((item) => {
        const image = item.wishlistImage
          ? `<img src="${this.escape(item.wishlistImage)}" alt="" loading="lazy">`
          : '<span class="monolyt-wishlist__placeholder"></span>';
        const itemRemoveLabel = item.wishlistTitle
          ? `${removeLabel}: ${item.wishlistTitle}`
          : removeLabel;

        return `
          <li class="monolyt-wishlist__item">
            <a href="${this.escape(item.wishlistUrl)}" class="monolyt-wishlist__item-link">
              <span class="monolyt-wishlist__media">${image}</span>
              <span class="monolyt-wishlist__meta">
                <span class="monolyt-wishlist__name">${this.escape(item.wishlistTitle)}</span>
                <span class="monolyt-wishlist__price">${this.escape(item.wishlistPrice || '')}</span>
              </span>
            </a>
            <button
              type="button"
              class="monolyt-wishlist__remove"
              data-wishlist-remove="${this.escape(item.handle)}"
              aria-label="${this.escape(itemRemoveLabel)}"
            >
              ${removeIcon}
            </button>
          </li>
        `;
      })
      .join('');
  }

  open() {
    const drawer = this.drawer;
    if (!drawer) return;

    drawer.hidden = false;
    drawer.setAttribute('aria-hidden', 'false');
    this.registerOverlay();
    this.headerComponent?.claimOverlay?.(this, { panel: this.panel, trigger: this.trigger });
    requestAnimationFrame(() => this.headerComponent?.syncOverlayGeometry?.(this));
  }

  isOpen() {
    const drawer = document.querySelector('[data-wishlist-drawer]');
    return Boolean(drawer && !drawer.hidden && drawer.getAttribute('aria-hidden') !== 'true');
  }

  toggleDrawer() {
    if (this.isOpen()) {
      this.close();
      return;
    }

    this.open();
  }

  close(options = {}) {
    const drawer = this.drawer;
    if (!drawer) return;
    drawer.hidden = true;
    drawer.setAttribute('aria-hidden', 'true');
    this.headerComponent?.releaseOverlay?.(this, { restoreFocus: options.restoreFocus === true });
  }

  escape(value = '') {
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }
}

if (!window.monolytWishlist) {
  window.monolytWishlist = new MonolytWishlist();
}
